import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useAppAuth } from '../components/AuthContext.jsx';
import { listSettings, saveSetting } from '../services/jrOverview.js';
import {
  MANDATORY_SOURCES, activePolicyVersion, canCreateCatalogueItem, isoDate, latestPolicyVersion, needsJchConsent,
  policyChanges, pragueTomorrow, samePolicy, validatePolicyDraft,
} from './policyAdmin.js';

function OtherSettings() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => {
    let active = true;
    listSettings().then((s) => { if (active) setRows(s.filter((r) => r.key !== 'drill_policy')); })
      .catch((e) => { if (active) setError(e.message || 'Další parametry se nepodařilo načíst.'); });
    return () => { active = false; };
  }, []);
  const save = async (setting) => {
    setSaving(true);
    setError('');
    setMsg('');
    try {
      const updated = await saveSetting(setting, draft[setting.key]);
      setRows((r) => r.map((s) => s.key === updated.key ? updated : s));
      setDraft((d) => { const next = { ...d }; delete next[setting.key]; return next; });
      setMsg('Parametr uložen.');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Uložení se nepodařilo.');
    } finally { setSaving(false); }
  };
  return <>
    {error && <p className="dashboard__error" role="alert">{error}</p>}
    {msg && <p className="dashboard__status" role="status">{msg}</p>}
    {rows.length > 0 && <fieldset className="card" disabled={saving}>
      <legend className="card__title">Další uložené parametry</legend>
      <p className="card__hint">Samostatné technické nastavení mimo verzované parametry a číselníky.
        Ukládá se po jednotlivých položkách, nikoli atomicky s datem platnosti níže.</p>
      {rows.map((s) => <div key={s.key}>
        <label className="auth__label">{s.description || s.key} ({s.key})
          {s.value_type === 'boolean' ? <select className="auth__input"
            value={draft[s.key] ?? String(s.value)}
            onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })}>
            <option value="true">ano</option><option value="false">ne</option>
          </select> : <input className="auth__input"
            value={draft[s.key] ?? (typeof s.value === 'string' ? s.value : JSON.stringify(s.value))}
            onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })} />}
        </label>
        <button type="button" className="button button--secondary" disabled={draft[s.key] === undefined}
          onClick={() => save(s)}>Uložit parametr</button>
      </div>)}
    </fieldset>}
  </>;
}

function Numeric({ label, value, onChange, min = 0, max, step = 'any', readOnly = false }) {
  return <label className="auth__label">{label}
    <input className="auth__input" type="number" min={min} max={max} step={step} required
      readOnly={readOnly} value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
  </label>;
}

function TextField({ label, value, onChange, ...props }) {
  return <label className="auth__label">{label}
    <input className="auth__input" required value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...props} />
  </label>;
}

function ActiveToggle({ item, onChange }) {
  return <label className="auth__label">
    <input type="checkbox" checked={item.active !== false} onChange={(e) => onChange({ active: e.target.checked })} />
    {' '}Nabízet v nových zápisech (vypnutím se skryje; historie zůstává)
  </label>;
}

function Sources({ items, original, onChange, isJr, dealTypes }) {
  const [code, setCode] = useState('');
  const [addError, setAddError] = useState('');
  const update = (index, patch) => onChange(items.map((item, i) => i === index ? { ...item, ...patch } : item));
  const add = () => {
    if (!/^[a-z][a-z0-9_]*$/.test(code) || items.some((item) => item.code === code)) {
      setAddError('Vyplňte jedinečný kód: malá písmena, číslice a podtržítko; začněte písmenem.');
      return;
    }
    onChange([...items, {
      code, name: '', unit: '', actionsPerContact: 1, mandatory: false, weeklyVolume: 0,
      meetingConversion: 0, dealType: items[0]?.dealType || 'sale', estimated: false, active: true,
    }]);
    setCode('');
    setAddError('');
  };
  const options = dealTypes?.length ? dealTypes : [
    { code: 'sale', name: 'Prodej' }, { code: 'rental', name: 'Nájem' },
  ];
  return <section className="card">
    <h2 className="card__title">Zdroje kontaktů</h2>
    <p className="card__hint">Kód je stálá identita, přejmenování nerozpojí historii. Existující zdroj nelze smazat.
      Nový zdroj přidává JR; nové nebo změněné počty akcí na kontakt vyžadují souhlas JCH.
      Počítají se jen celé kontakty za den. Konverze a model jsou pouze pro manažerský přístup.</p>
    {items.map((item, index) => <fieldset className="card" key={item.code}>
      <legend>{item.name || 'Nový zdroj'} — {item.code}{item.estimated ? ' · odhad' : ''}</legend>
      <p>Stálý kód: <code>{item.code}</code>{!original.some((s) => s.code === item.code) ? ' · nový' : ''}</p>
      <TextField label="Název zdroje" value={item.name} maxLength={120} onChange={(name) => update(index, { name })} />
      <TextField label="Jednotka" value={item.unit} maxLength={80} onChange={(unit) => update(index, { unit })} />
      <Numeric label="Akcí na 1 kontakt" value={item.actionsPerContact} min={1} max={1000000} step={1}
        onChange={(actionsPerContact) => update(index, { actionsPerContact })} />
      <label className="auth__label">
        <input type="checkbox" checked={Boolean(item.mandatory)} disabled={!MANDATORY_SOURCES.has(item.code)}
          onChange={(e) => update(index, { mandatory: e.target.checked })} /> Povinný zdroj
      </label>
      <Numeric label="Výchozí týdenní objem" value={item.weeklyVolume} max={1000000} step={1}
        onChange={(weeklyVolume) => update(index, { weeklyVolume })} />
      <Numeric label="Kontakt → schůzka (podíl 0–1, např. 0,1 = 10 %)" value={item.meetingConversion} max={1}
        onChange={(meetingConversion) => update(index, { meetingConversion })} />
      <p className="card__hint">Konverze: {item.meetingConversion > 0
        ? `1 z ${(1 / item.meetingConversion).toLocaleString('cs-CZ', { maximumFractionDigits: 8 })}`
        : 'bez konverze'}{item.estimated ? ' (odhad)' : ''}. Ukládá se plná zadaná přesnost.</p>
      <label className="auth__label">Typ obchodu
        <select className="auth__input" value={item.dealType} onChange={(e) => update(index, { dealType: e.target.value })}>
          {!options.some((option) => option.code === item.dealType) && <option value={item.dealType}>{item.dealType}</option>}
          {options.map((option) => <option key={option.code} value={option.code}>{option.name}</option>)}
        </select>
      </label>
      <label className="auth__label"><input type="checkbox" checked={Boolean(item.estimated)}
        onChange={(e) => update(index, { estimated: e.target.checked })} /> Konverze je odhad</label>
      <ActiveToggle item={item} onChange={(patch) => update(index, patch)} />
      {!original.some((entry) => entry.code === item.code) && <button type="button" className="button button--secondary"
        onClick={() => onChange(items.filter((_, i) => i !== index))}>Zrušit přidání neuloženého zdroje</button>}
    </fieldset>)}
    {isJr ? <div>
      <label className="auth__label">Stálý kód nového zdroje
        <input className="auth__input" value={code} maxLength={64} onChange={(e) => setCode(e.target.value)} placeholder="novy_zdroj" />
      </label>
      <button className="button button--secondary" type="button" onClick={add}>Přidat zdroj do návrhu</button>
      {addError && <p role="alert" className="dashboard__error">{addError}</p>}
    </div> : <p className="card__hint">Nový zdroj může přidat pouze JR.</p>}
  </section>;
}

function Catalogue({ definition, items, original, onChange }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const fields = definition.fields?.length ? definition.fields : [{ key: 'name', label: 'Název', type: 'text' }];
  const update = (index, patch) => onChange(items.map((item, i) => i === index ? { ...item, ...patch } : item));
  const add = () => {
    if (!canCreateCatalogueItem(definition)) return;
    if (!/^[a-z][a-z0-9_]*$/.test(code) || items.some((item) => item.code === code)) {
      setError('Zadejte jedinečný stálý kód (malá písmena, číslice, podtržítko; začátek písmenem).');
      return;
    }
    const item = { code, name: '', active: true };
    for (const field of fields) {
      if (!(field.key in item)) item[field.key] = field.default
        ?? (field.type === 'boolean' ? false : field.type === 'number' ? 0 : '');
    }
    onChange([...items, item]);
    setCode('');
    setError('');
  };
  return <section className="card">
    <h2 className="card__title">{definition.title}</h2>
    {definition.description && <p className="card__hint">{definition.description}</p>}
    {items.map((item, index) => <fieldset className="card" key={item.code}>
      <legend>{item.name || 'Nová položka'} — {item.code}</legend>
      {fields.filter((field) => !['code', 'active'].includes(field.key)).map((field) => {
        const change = (value) => update(index, { [field.key]: value });
        if (field.type === 'boolean') return <label className="auth__label" key={field.key}>
          <input type="checkbox" checked={Boolean(item[field.key])} onChange={(e) => change(e.target.checked)} /> {field.label}
        </label>;
        if (field.type === 'number') return <Numeric key={field.key} {...field} label={field.label}
          value={item[field.key]} onChange={change} />;
        if (field.options) return <label className="auth__label" key={field.key}>{field.label}
          <select className="auth__input" required value={item[field.key] ?? ''} onChange={(e) => change(e.target.value)}>
            <option value="">Vyberte…</option>
            {field.options.map((option) => <option key={option.value ?? option.code ?? option}
              value={option.value ?? option.code ?? option}>{option.label ?? option.name ?? option}</option>)}
          </select>
        </label>;
        return <TextField key={field.key} label={field.label} value={item[field.key]} maxLength={field.type === 'url' ? undefined : 120}
          type={field.type === 'url' ? 'url' : 'text'} onChange={change} />;
      })}
      <ActiveToggle item={item} onChange={(patch) => update(index, patch)} />
      {!original.some((entry) => entry.code === item.code) && <button type="button" className="button button--secondary"
        onClick={() => onChange(items.filter((_, i) => i !== index))}>Zrušit přidání neuložené položky</button>}
    </fieldset>)}
    {canCreateCatalogueItem(definition) ? <>
      <label className="auth__label">Stálý kód nové položky
        <input className="auth__input" value={code} maxLength={64} onChange={(e) => setCode(e.target.value)} />
      </label>
      <button className="button button--secondary" type="button" onClick={add}>Přidat položku do návrhu</button>
    </> : <p className="card__hint">Položky mají pevné identity navázané na pracovní postupy systému.
      Lze upravit jejich názvy a skrýt je pro nové zápisy, ale nelze přidávat nové typy nebo stavy s nepodporovaným významem.</p>}
    {error && <p className="dashboard__error" role="alert">{error}</p>}
    <p className="card__hint">Existující kódy jsou neměnné. Položky se nemažou, pouze skryjí pro nové zápisy.</p>
  </section>;
}

function CalendarEditor({ calendar, onChange }) {
  const holidays = calendar.holidays || [];
  const periods = calendar.quietPeriods || [];
  const edit = (key, index, patch) => onChange({
    ...calendar, [key]: calendar[key].map((item, i) => i === index ? { ...item, ...patch } : item),
  });
  return <section className="card">
    <h2 className="card__title">Kalendář svátků a tichá období</h2>
    <p className="card__hint">Země: ČR (CZ). České zákonné svátky včetně pohyblivých svátků dopočítává server automaticky.
      Níže jsou vlastní doplnění / přepsání názvu na konkrétní datum; odebrání vlastní položky neruší zákonný svátek.
      Pracovní týden je pondělí–pátek, začíná v pondělí. Tiché období má plán 0.
      Změny ovlivní plán a krácení až od platnosti celé verze, ne uzavřenou historii.</p>
    <h3>Vlastní svátky</h3>
    {holidays.map((holiday, index) => <div key={index}>
      <TextField label="Datum svátku" type="date" value={holiday.date} onChange={(date) => edit('holidays', index, { date })} />
      <TextField label="Název svátku" value={holiday.name} onChange={(name) => edit('holidays', index, { name })} />
      <button type="button" className="button button--secondary"
        onClick={() => onChange({ ...calendar, holidays: holidays.filter((_, i) => i !== index) })}>Odebrat vlastní svátek</button>
    </div>)}
    <button type="button" className="button button--secondary"
      onClick={() => onChange({ ...calendar, holidays: [...holidays, { date: '', name: '' }] })}>Přidat vlastní svátek</button>
    <h3>Tichá období (včetně obou krajních dnů)</h3>
    {periods.map((period, index) => <fieldset key={period.id} className="card">
      <legend>{period.name || 'Nové tiché období'}</legend>
      <TextField label="Název tichého období" value={period.name} onChange={(name) => edit('quietPeriods', index, { name })} />
      <TextField label="Od" type="date" value={period.from} onChange={(from) => edit('quietPeriods', index, { from })} />
      <TextField label="Do" type="date" value={period.to} min={period.from || undefined}
        onChange={(to) => edit('quietPeriods', index, { to })} />
      <button type="button" className="button button--secondary"
        onClick={() => onChange({ ...calendar, quietPeriods: periods.filter((_, i) => i !== index) })}>Odebrat tiché období</button>
    </fieldset>)}
    <button type="button" className="button button--secondary" onClick={() => onChange({
      ...calendar, quietPeriods: [...periods, { id: crypto.randomUUID(), name: '', from: '', to: '' }],
    })}>Přidat tiché období</button>
  </section>;
}

const displayValue = (value) => value === undefined ? '(nenastaveno)' : JSON.stringify(value);

function VersionHistory({ versions, active }) {
  const ordered = versions;
  return <section className="card">
    <h2 className="card__title">Historie verzí — kdo, kdy, co a platnost</h2>
    {!ordered.length && <p>Dosud nebyla uložena žádná verze; používá se výchozí nastavení.</p>}
    {[...ordered].reverse().map((version) => {
      const index = ordered.indexOf(version);
      const previous = ordered[index - 1];
      const changes = previous ? policyChanges(previous.policy, version.policy) : [];
      return <details key={version.id}>
        <summary>Verze {version.id} · platí od {isoDate(version.effective_from)} · {version.actor_name || version.created_by || 'výchozí nastavení'}</summary>
        <p>Uloženo: {version.created_at ? new Date(version.created_at).toLocaleString('cs-CZ') : 'neuvedeno'}
          {samePolicy(active, version.policy) ? ' · odpovídá aktuálnímu nastavení' : ''}</p>
        {version.jch_consent_recorded && <p>Zadávající JR při uložení potvrdil souhlas JCH s převodem akcí na kontakt.</p>}
        {previous ? <>
          <p>Změny proti verzi {previous.id} (původní → nová hodnota):</p>
          {changes.length ? <ul>{changes.map((change) => <li key={change.path}>
            <code>{change.path}</code>: {displayValue(change.before)} → {displayValue(change.after)}
          </li>)}</ul> : <p>Beze změny hodnot.</p>}
        </> : <p>První uložený snímek; předchozí verze není v historii dostupná.</p>}
        <details><summary>Úplné nastavení této verze (jen pro čtení)</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(version.policy, null, 2)}</pre>
        </details>
      </details>;
    })}
  </section>;
}

const GROUP_HINTS = {
  trafficLight: 'Prahy semaforu platí pro nově hodnocená období; pod oranžovou je červená, nulový cíl je bez barvy. Uzavřené týdny se nepřepočítávají.',
  weeklyDefaults: 'Předvyplnění nových plánů; stávající individuální plány se zpětně nemění.',
  calendarRules: 'Krácení plánu podle pracovních dnů a minimální hranice ručního snížení. Pod minimum jen se souhlasem JCH.',
  goals: 'Cílové tempo, milníky a ukazatele. Laťka samostatnosti je sdílený parametr; kritérium Samostatný se samostatně nenastavuje.',
  ramp: 'Stupně náběhu, předvyplnění schůzek a náborů a krácení v plánu.',
  conversion: 'Model, diagnostika a rozjednáno (jen manažerský přístup). Ukládá se plná zadaná přesnost; přepočet orientačních cílů z konverzí zůstává podle stávajících pravidel (D-25).',
  progression: 'Dokumentovaná pravidla doporučení úrovně. Zařazení vždy potvrzuje JR/JCH.',
  operations: 'Příprava a provoz, schůzky a plánování. Nevyřešená pravidla zůstávají beze změny.',
  kpi: 'Cílové hodnoty ukazatelů a vyhodnocení systému.',
};

export default function Parametry() {
  const { user } = useAppAuth();
  const [admin, setAdmin] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [reloadRequested, setReloadRequested] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState(() => pragueTomorrow());
  const [jchConsent, setJchConsent] = useState(false);
  const minimumDate = pragueTomorrow();
  const dirty = Boolean(draft && admin && !samePolicy(draft, admin.latest));
  const consentRequired = Boolean(draft && admin && needsJchConsent(admin.latest, draft, user?.role));

  const accept = (data) => {
    if (!data?.active || !data?.latest || !Array.isArray(data.groups) || !Array.isArray(data.versions)
      || !Array.isArray(data.catalogueDefinitions)) throw new Error('Server vrátil neúplná data správy parametrů.');
    setAdmin(data);
    setDraft(structuredClone(data.latest));
    setJchConsent(false);
    setConflict(false);
    setReloadRequested(false);
  };
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get('/system-settings/policy/admin')
      .then((r) => { if (active) accept(r.data.data); })
      .catch((e) => { if (active) setError(e.message || 'Parametry se nepodařilo načíst.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt, user?.id]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const update = (group, key, value) => setDraft((d) => ({ ...d, [group]: { ...d[group], [key]: value } }));
  const updateLevel = (index, patch) => setDraft((d) => ({
    ...d, levels: d.levels.map((level, i) => i === index ? { ...level, ...patch } : level),
  }));
  const updateCatalogue = (key, items) => setDraft((d) => ({ ...d, catalogues: { ...d.catalogues, [key]: items } }));
  const save = async (event) => {
    event.preventDefault();
    setError('');
    setMsg('');
    const validation = validatePolicyDraft(draft, admin.groups, effectiveFrom, pragueTomorrow());
    if (validation) { setError(validation); return; }
    if (consentRequired && !jchConsent) { setError('Změnu počtu akcí na kontakt musí schválit JCH. Potvrďte jeho souhlas.'); return; }
    setSaving(true);
    try {
      const latestVersion = latestPolicyVersion(admin.versions);
      const response = await api.patch('/system-settings/policy', {
        policy: draft, effectiveFrom, baseVersionId: latestVersion?.id ?? null, jchConsent,
      });
      accept(response.data.data);
      setMsg(`Celý návrh byl uložen jako verze s platností od ${effectiveFrom}. Dnešní aktivní nastavení se nemění.
        Minulé týdny, barvy ani uložené návrhy úrovní se nepřepočítávají.`);
    } catch (e) {
      const isConflict = e.response?.status === 409 || e.status === 409;
      setConflict(isConflict);
      setError(isConflict ? 'Nastavení mezitím změnil jiný správce. Váš návrh zůstává zachován; porovnejte jej s novou verzí a znovu načtěte data.'
        : e.response?.data?.error || e.message || 'Celý návrh se nepodařilo uložit. Změny zůstávají v návrhu.');
    } finally { setSaving(false); }
  };
  if (loading) return <p className="dashboard__status" role="status">Načítají se verze parametrů a číselníky…</p>;
  if (!admin || !draft) return <section className="dashboard">
    <h1>Parametry a číselníky</h1><p role="alert" className="dashboard__error">{error}</p>
    <button type="button" className="button button--secondary" onClick={() => setAttempt((a) => a + 1)}>Zkusit znovu</button>
  </section>;
  const pending = admin.versions.filter((version) => isoDate(version.effective_from) >= minimumDate);
  const today = new Date(`${minimumDate}T12:00:00Z`);
  today.setUTCDate(today.getUTCDate() - 1);
  const activeVersion = activePolicyVersion(admin.versions, today.toISOString().slice(0, 10));
  return <div className="dashboard">
    <div className="dashboard__header"><div>
      <h1 className="dashboard__title">Parametry a číselníky</h1>
      <p className="dashboard__lead">JR a JCH společně · jedno atomické uložení všech parametrů, kalendáře a číselníků.</p>
    </div></div>
    {error && <p className="dashboard__error" role="alert">{error}</p>}
    {msg && <p className="dashboard__status" role="status">{msg}</p>}
    <section className="card">
      <h2 className="card__title">Aktivní nastavení a naplánované změny</h2>
      <p>Dnes platí {activeVersion ? `verze ${activeVersion.id} od ${isoDate(activeVersion.effective_from)}` : 'výchozí aktivní nastavení'}.
        {' '}Editor vychází z poslední uložené verze, včetně budoucích změn.</p>
      {pending.length ? <ul>{pending.map((version) => <li key={version.id}>
        Naplánovaná verze {version.id} od {isoDate(version.effective_from)}
        {' · '}{version.actor_name || version.created_by || 'správce'}
      </li>)}</ul> : <p>Žádná budoucí verze není naplánována.</p>}
      <details><summary>Úplné dnes aktivní nastavení (jen pro čtení)</summary>
        <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(admin.active, null, 2)}</pre>
      </details>
      <button className="button button--secondary" type="button" disabled={saving} onClick={() => {
        if (dirty) setReloadRequested(true);
        else setAttempt((a) => a + 1);
      }}>{conflict ? 'Načíst novou verzi po konfliktu' : 'Znovu načíst ze serveru'}</button>
      {reloadRequested && <div role="alert">
        <p>Nové načtení zahodí všechny neuložené změny. Pokračovat?</p>
        <button type="button" className="button button--secondary" onClick={() => setAttempt((a) => a + 1)}>Zahodit návrh a načíst</button>
        <button type="button" className="button button--secondary" onClick={() => setReloadRequested(false)}>Ponechat návrh</button>
      </div>}
    </section>
    <form onSubmit={save}>
      <fieldset className="card" disabled={saving}>
        <legend className="card__title">Společná platnost a uložení</legend>
        <p role="status">{dirty ? '● Neuložené změny' : 'Žádné neuložené změny'}</p>
        <TextField label="Platí od (Europe/Prague)" type="date" value={effectiveFrom} min={minimumDate} onChange={setEffectiveFrom} />
        <p className="card__hint">Nejdříve zítra. Změna platí jen dopředu; uzavřené týdny, barvy a uložené návrhy úrovní zůstávají zachované.
          Nevyřešená pravidla se tímto editorem nemění. Oznámení změn a offline režim nejsou součástí tohoto nastavení.</p>
        {consentRequired && <label className="auth__label">
          <input type="checkbox" checked={jchConsent} required onChange={(e) => setJchConsent(e.target.checked)} />
          {' '}Potvrzuji souhlas JCH s novým nebo změněným počtem akcí na kontakt.
        </label>}
        <button className="button button--spot" disabled={!dirty || saving || conflict || (consentRequired && !jchConsent)} type="submit">
          {saving ? 'Ukládá se celý návrh…' : 'Uložit všechny změny s platností'}
        </button>
      </fieldset>
      <fieldset disabled={saving} style={{ border: 0, padding: 0, minWidth: 0 }}>
        <legend className="card__title">Návrh poslední verze</legend>
        {admin.groups.map((group) => <section className="card" key={group.key}>
          <h2 className="card__title">{group.title}</h2>
          <p className="card__hint">{group.description || group.impact || GROUP_HINTS[group.key]
            || 'Změna se projeví v navazujícím plánování a hodnocení od data platnosti, nikoli zpětně.'}</p>
          {group.fields.map((field) => {
            const value = draft[group.key]?.[field.key] ?? field.default;
            const ratio = group.key === 'conversion' && value > 0
              ? field.key.endsWith('Rate') ? 1 / value
                : /PerMeeting|PerAcquisition/.test(field.key) ? value : null
              : null;
            return <div key={field.key}>
              <Numeric {...field} label={field.label} value={value}
                step={group.key === 'conversion' && field.step !== 1 ? 'any' : field.step}
                onChange={(next) => update(group.key, field.key, next)} />
              {ratio != null && <p className="card__hint">1 z {ratio.toLocaleString('cs-CZ', { maximumFractionDigits: 12 })}</p>}
            </div>;
          })}
        </section>)}
        <section className="card">
          <h2 className="card__title">Fáze a úrovně — názvy, barvy a původní volitelná kritéria</h2>
          <p className="card__hint">Stálá čísla úrovní se nemění. Dokumentovaná pravidla jsou ve skupině Kritéria úrovní výše
            (nábory nebo první provize; provize za 3 nebo 6 měsíců; Samostatný odkazuje na laťku samostatnosti).
            Níže jsou zachována starší volitelná kritéria, ne náhrada dokumentovaných pravidel.
            Doporučení nikdy automaticky nemění zařazení; potvrzuje ho JR/JCH.</p>
          {(draft.levels || []).map((level, i) => <fieldset className="card" key={level.numeric}>
            <legend>Úroveň {level.numeric} — {level.name}</legend>
            <TextField label="Název úrovně" value={level.name} maxLength={50} onChange={(name) => updateLevel(i, { name })} />
            <TextField label="Barva úrovně" type="color" value={level.color} onChange={(color) => updateLevel(i, { color })} />
            {i > 0 && <>
              <label className="auth__label"><input type="checkbox" checked={Boolean(level.criteria)}
                onChange={(e) => updateLevel(i, { criteria: e.target.checked
                  ? { minAcquisitions3m: 0, minCommission12m: 0, maxRedWeeks6: 0 } : null })} />
                {' '}Používat původní volitelná kritéria (všechna současně, šest uzavřených hodnocených týdnů)
              </label>
              {level.criteria && <>
                <Numeric label="Minimálně náborů za 3 měsíce" value={level.criteria.minAcquisitions3m} step={1}
                  onChange={(value) => updateLevel(i, { criteria: { ...level.criteria, minAcquisitions3m: value } })} />
                <Numeric label="Minimálně provizí za 12 měsíců (Kč)" value={level.criteria.minCommission12m} step={0.01}
                  onChange={(value) => updateLevel(i, { criteria: { ...level.criteria, minCommission12m: value } })} />
                <Numeric label="Maximálně červených týdnů z posledních 6" value={level.criteria.maxRedWeeks6} max={6} step={1}
                  onChange={(value) => updateLevel(i, { criteria: { ...level.criteria, maxRedWeeks6: value } })} />
              </>}
            </>}
          </fieldset>)}
        </section>
        <Sources items={draft.catalogues?.sources || []} original={admin.latest.catalogues?.sources || []}
          onChange={(items) => updateCatalogue('sources', items)} isJr={user?.role === 'JR'} dealTypes={draft.catalogues?.dealTypes} />
        {admin.catalogueDefinitions.filter((definition) => definition.key !== 'sources').map((definition) =>
          <Catalogue key={definition.key} definition={definition} items={draft.catalogues?.[definition.key] || []}
            original={admin.latest.catalogues?.[definition.key] || []}
            onChange={(items) => updateCatalogue(definition.key, items)} />)}
        <CalendarEditor calendar={draft.calendar || { country: 'CZ', holidays: [], quietPeriods: [] }}
          onChange={(calendar) => setDraft((d) => ({ ...d, calendar }))} />
      </fieldset>
      <p role="status">{dirty ? '● Návrh obsahuje neuložené změny.' : 'Návrh odpovídá poslední uložené verzi.'}</p>
      <button className="button button--spot" disabled={!dirty || saving || conflict || (consentRequired && !jchConsent)} type="submit">
        {saving ? 'Ukládá se…' : 'Uložit celý návrh'}
      </button>
      {dirty && <details>
        <summary>Neuložené změny proti poslední verzi (původní → nová hodnota)</summary>
        <ul>{policyChanges(admin.latest, draft).map((change) => <li key={change.path}>
          <code>{change.path}</code>: {displayValue(change.before)} → {displayValue(change.after)}
        </li>)}</ul>
        <details><summary>Úplný neuložený návrh (jen pro čtení, lze zkopírovat před novým načtením)</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(draft, null, 2)}</pre>
        </details>
      </details>}
    </form>
    <VersionHistory versions={admin.versions} active={admin.active} />
    <OtherSettings />
  </div>;
}
